import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "../../lib/usePageMeta";
import { ChevronDown, ShieldCheck, ArrowRight } from "lucide-react";

/**
 * CCCHAPS Frequently Asked Questions
 * Route: /faq
 * Two audiences: For Organizations & HR (22 questions) and For Employees (15 questions).
 * Accordion pattern uses semantic <button> controls with aria-expanded / aria-controls
 * so it remains keyboard operable and screen-reader friendly.
 */
const ORG_HR_FAQS = [
  {
    q: "What is workplace chaplaincy?",
    a: "Workplace chaplaincy is voluntary, relationship-based, non-clinical support offered inside the work environment. A CCCHAPS chaplain is a caring, trained presence available to listen and support employees through personal, family, workplace, grief, caregiving, spiritual, and other life concerns. Chaplaincy complements — rather than replaces — HR, an EAP, licensed therapists, medical providers, and emergency services.",
  },
  {
    q: "How is CCCHAPS introduced to employees?",
    a: "Implementation is shaped around each organization. Introductions may include employee orientation moments, organization-specific communications, Care Portal information, CALLACHAP access instructions, and clear guidance on how employees can voluntarily reach chaplain support. There is no single rigid rollout — CCCHAPS works with HR and leadership to introduce the service in a way that fits the culture and the workforce.",
  },
  {
    q: "Is chaplain care voluntary?",
    a: "Yes. Employees decide whether to participate, when to engage, and what they wish to discuss. No one is required to speak with a chaplain, and there is no attendance tracking of individual engagement.",
  },
  {
    q: "What if an employee does not want to speak with a chaplain?",
    a: "Their decision is respected. CCCHAPS care is permission-based. Employees may decline any interaction and may choose to reach out later if their preferences or circumstances change. The chaplain remains a steady, available option — never a required interaction.",
  },
  {
    q: "How does a CCCHAPS chaplain care for employees?",
    a: "Through compassionate presence, listening, encouragement, and relationship-based support. Depending on the situation, care may include grief support, practical support, spiritual support when the employee invites it, help identifying appropriate resources, and follow-up when appropriate. Depending on the organization's care arrangement, support may be delivered through scheduled on-site presence, phone, text, confidential messages, and available remote-support pathways.",
  },
  {
    q: "What kinds of situations can employees bring to a chaplain?",
    a: "Grief and loss, marriage and relationships, parenting and family, caregiving, stress, illness and hospitalization, financial hardship, major life changes, workplace concerns, loneliness, difficult personal circumstances, spiritual or faith questions when the employee chooses, crisis and traumatic events, or simply needing someone to listen. Chaplains do not diagnose or treat clinical conditions — the focus is human, supportive care.",
  },
  {
    q: "Does CCCHAPS provide therapy or long-term counseling?",
    a: "No. CCCHAPS chaplains provide non-clinical supportive care — not psychotherapy, medical treatment, or long-term clinical counseling. When needs exceed chaplain scope, chaplains may help employees identify appropriate EAP resources, licensed professionals, medical providers, community resources, or emergency services.",
  },
  {
    q: "When are CCCHAPS chaplains available?",
    a: "CCCHAPS provides 24/7 on-call chaplain access through participating organization care arrangements. Depending on the organization's arrangement, employees may reach a chaplain through configured phone, text, confidential message, available remote support, and scheduled on-site presence. Response times may vary by channel — 24/7 on-call does not promise instant response through every channel.",
  },
  {
    q: "How does CCCHAPS work with HR and management?",
    a: "CCCHAPS works alongside organizational leadership while maintaining appropriate boundaries around individual employee conversations. Chaplains may support employee care, crisis response, grief and bereavement, leadership support, organizational communication when appropriate, and resource coordination. Chaplains are not supervisors, investigators, disciplinary authorities, or replacements for HR.",
  },
  {
    q: "What does confidentiality mean in workplace chaplaincy?",
    a: "Chaplain conversations are treated as confidential within appropriate professional, ethical, safety, legal, and organizational-policy boundaries. Employees are welcome to ask a chaplain about those boundaries before sharing sensitive information. CCCHAPS does not use absolute confidentiality promises because responsible chaplain care always accounts for safety and legal responsibilities.",
  },
  {
    q: "What information does my organization receive?",
    a: "Organizations may receive aggregate, non-identifying utilization information and program-level reporting consistent with their care arrangement. This helps leadership understand overall program utilization and broad support patterns without turning individual chaplain conversations into employer case files.",
  },
  {
    q: "What information does my organization NOT receive?",
    a: "Private details from an individual employee's chaplain conversation are not part of routine employer reporting. Names, contact information, message content, and chaplain notes are not routinely shared with the employer. The organization sees the impact. The employee keeps the conversation. Appropriate confidentiality, safety, and legal boundaries continue to apply.",
  },
  {
    q: "How does workplace chaplaincy complement an EAP?",
    a: (
      <>
        Chaplaincy and EAPs have different but complementary roles. Chaplaincy provides accessible,
        relationship-based, non-clinical human support. An EAP typically provides structured clinical
        counseling and, depending on the plan, legal, financial, and other professional services. A
        chaplain may help an employee recognize when another professional resource is appropriate. CCCHAPS
        does not replace an EAP. For a fuller comparison, see the{" "}
        <Link to="/resources/chaplaincy-vs-eap" className="text-[#4A6FA5] underline hover:text-[#3B597B]" data-testid="faq-link-chap-vs-eap">
          Chaplaincy vs EAP
        </Link>{" "}
        resource.
      </>
    ),
  },
  {
    q: "Can employees' family members receive support?",
    a: "Where included in the organization's care arrangement, certain chaplain support and eligible family resources may extend to immediate family members. Specific family-support eligibility depends on the arrangement — HR can confirm what is included for their workforce.",
  },
  {
    q: "What is CALLACHAP?",
    a: (
      <>
        CALLACHAP provides organization-configured pathways for employees to reach their CCCHAPS chaplain
        team. Depending on the organization's care arrangement, pathways may include phone, text, confidential
        message, and available remote support. Learn more on the{" "}
        <Link to="/callachap" className="text-[#4A6FA5] underline hover:text-[#3B597B]" data-testid="faq-link-callachap-org">
          CALLACHAP
        </Link>{" "}
        page.
      </>
    ),
  },
  {
    q: "What is CCCHAPS On Demand?",
    a: "CCCHAPS On Demand is a digital-resource option available to eligible employees and families of participating organizations. Employees can privately explore practical resources — short videos and articles built for real life — at their own pace and on their own schedule. Access is set up through each participating organization.",
  },
  {
    q: "What happens when an organization experiences a death, crisis, or serious event?",
    a: "CCCHAPS can provide enhanced crisis, grief, bereavement, and organizational support consistent with the organization's care arrangement. Support may include chaplain presence, employee support, leadership consultation, grief support, and appropriate coordination with HR and leadership. Specific response scope and cadence are shaped by the arrangement and the situation.",
  },
  {
    q: "What qualifications and training do CCCHAPS chaplains have?",
    a: "CCCHAPS chaplains are prepared for compassionate, non-clinical workplace care and operate within defined professional and ethical boundaries. CCCHAPS also invests in ongoing chaplain development through the CCCHAPS Chaplain Training Academy. Specific credentialing expectations for any given care arrangement can be discussed with CCCHAPS during the exploratory conversation.",
  },
  {
    q: "Can CCCHAPS chaplains support people from different faiths or no faith at all?",
    a: "Yes. CCCHAPS care is voluntary, respectful, and permission-based. Employees do not have to practice a religion to receive chaplain support. Any spiritual or religious discussion follows the individual's own beliefs, preferences, and permission. Employees are never pressured toward a particular belief.",
  },
  {
    q: "How are CCCHAPS services funded?",
    a: (
      <>
        Organizations contract with CCCHAPS to provide chaplain care arrangements for their workforce.
        Employees do not individually pay for ordinary chaplain support included through their participating
        organization's arrangement. Care-arrangement design and pricing are shaped around each organization
        — talk with CCCHAPS to explore what fits. See{" "}
        <Link to="/packages" className="text-[#4A6FA5] underline hover:text-[#3B597B]" data-testid="faq-link-packages">
          Care Team Packages
        </Link>{" "}
        for an overview of how arrangements are structured.
      </>
    ),
  },
  {
    q: "Why outsource workplace chaplaincy instead of hiring an internal chaplain?",
    a: "Outsourcing to CCCHAPS gives an organization an established chaplain-care structure with defined professional boundaries, program administration, Care Portal infrastructure, CALLACHAP access, aggregate organizational reporting, and flexible care-arrangement design that complements existing HR and EAP resources. Whether outsourcing or an internal chaplain is right depends on each organization's size, structure, and goals — CCCHAPS is happy to talk through fit.",
  },
  {
    q: "What size organizations can CCCHAPS serve?",
    a: "CCCHAPS can discuss care arrangements with organizations of different sizes and structures. The appropriate arrangement depends on factors such as workforce size, locations, desired chaplain presence, support channels, and organizational needs. Contact CCCHAPS to explore what fits your organization.",
  },
];

const EMPLOYEE_FAQS = [
  {
    q: "What is a workplace chaplain?",
    a: "A workplace chaplain is a trained caregiver providing personal, compassionate, non-clinical support to people in the workplace. They are available to listen, encourage, and support employees through the things life sometimes brings.",
  },
  {
    q: "Do I have to talk with a chaplain?",
    a: "No. Chaplain care is voluntary. You decide whether to participate, when to reach out, and what you wish to discuss.",
  },
  {
    q: "Is my conversation with a chaplain confidential?",
    a: "Chaplain conversations are treated as confidential within appropriate professional, ethical, safety, legal, and organizational-policy boundaries. You are welcome to ask your chaplain about those boundaries before sharing sensitive information.",
  },
  {
    q: "Will my employer know what I talked about?",
    a: "Private details from your individual chaplain conversation are not routinely reported to your employer. Organizations may receive aggregate, non-identifying program information for planning purposes. The organization sees the impact. The employee keeps the conversation.",
  },
  {
    q: "Do I have to be religious?",
    a: "No. Employees of all faiths, beliefs, backgrounds — and those with no religious affiliation — may receive chaplain care.",
  },
  {
    q: "Will a chaplain try to convert me or change my beliefs?",
    a: "No. Care is permission-based and respectful of your beliefs and boundaries. Religious or spiritual discussion happens when you want it — not because a chaplain steers a conversation that way.",
  },
  {
    q: "What can I talk with a chaplain about?",
    a: "Grief. Family. Relationships. Parenting. Caregiving. Stress. Illness. Financial difficulty. Workplace concerns. Major life changes. Spiritual questions. Loneliness. Or simply needing someone to listen. There is no minimum for what qualifies as worth talking about.",
  },
  {
    q: "Can I talk about something happening outside of work?",
    a: "Yes. Personal and family circumstances do not stop at the workplace door. You are welcome to bring appropriate life concerns to a chaplain even when those circumstances started outside work.",
  },
  {
    q: "Can my family receive support?",
    a: "Depending on your organization's care arrangement, certain chaplain support and family resources may be available to immediate family members. You can check your organization-specific Care Portal information or ask your CCCHAPS chaplain about what family support is available.",
  },
  {
    q: "How do I contact a chaplain?",
    a: (
      <>
        You use the pathways configured for your organization. Depending on the arrangement, these may
        include phone, text, confidential message, available remote support, and scheduled on-site
        interaction. Your{" "}
        <Link to="/care-portal/callachap" className="text-[#4A6FA5] underline hover:text-[#3B597B]" data-testid="faq-link-carecallachap">
          CALLACHAP
        </Link>{" "}
        page and{" "}
        <Link to="/care-portal/start-here" className="text-[#4A6FA5] underline hover:text-[#3B597B]" data-testid="faq-link-starthere">
          Start Here
        </Link>{" "}
        introduction inside the Care Portal walk through the options available to you.
      </>
    ),
  },
  {
    q: "When can I contact a chaplain?",
    a: "CCCHAPS provides 24/7 on-call chaplain access through participating organization care arrangements. Response times may vary by channel. Scheduled on-site visits follow your organization's care arrangement.",
  },
  {
    q: "What is CALLACHAP?",
    a: "CALLACHAP provides convenient organization-configured pathways for you to reach your CCCHAPS chaplain team. Available channels depend on your organization's arrangement — only the channels configured for your organization will appear as active options.",
  },
  {
    q: "What is CCCHAPS On Demand?",
    a: "Eligible employees and families of participating organizations may have access to self-directed digital resources — short videos and articles you can privately explore at your own pace. Your organization sets up access when included in the arrangement.",
  },
  {
    q: "Is a chaplain a therapist?",
    a: "No. A chaplain provides non-clinical emotional, spiritual, relational, grief, crisis, and practical support within chaplain scope. Chaplains do not replace licensed mental-health or medical care.",
  },
  {
    q: "What if I need help beyond what a chaplain can provide?",
    a: "Your chaplain can help you identify appropriate resources — an EAP, a licensed professional, a medical provider, a community resource, or emergency support when the situation calls for it. Chaplains are not a barrier to further care; they are a starting point when you are not sure who to reach.",
  },
];

export default function FAQPage() {
  usePageMeta({
    title: "FAQ | California Corporate Chaplains",
    description:
      "Answers about workplace chaplaincy from CCCHAPS — how it works, what employees can expect, how privacy is protected, and how organizations bring chaplain care to their workforce.",
  });

  const [audience, setAudience] = useState("org");

  return (
    <main className="bg-white" data-testid="faq-page">
      {/* Hero */}
      <section className="pt-14 md:pt-20 pb-8 md:pb-10 border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]" data-testid="faq-eyebrow">
            Frequently Asked Questions
          </p>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance" data-testid="faq-heading">
            Questions About Workplace Chaplaincy
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed max-w-2xl">
            Whether you're considering chaplain care for your organization or wondering what support looks like as an employee, these answers explain how CCCHAPS works, what you can expect, and how privacy is protected.
          </p>
        </div>
      </section>

      {/* Audience tabs */}
      <section className="pt-8 md:pt-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div role="tablist" aria-label="FAQ audience" className="inline-flex flex-wrap gap-2 rounded-xl bg-slate-100 p-1.5" data-testid="faq-audience-tabs">
            {[
              { key: "org", label: "For Organizations & HR" },
              { key: "emp", label: "For Employees" },
            ].map((t) => {
              const active = audience === t.key;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={active}
                  aria-controls={`faq-panel-${t.key}`}
                  id={`faq-tab-${t.key}`}
                  data-testid={`faq-tab-${t.key}`}
                  onClick={() => setAudience(t.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A6FA5] focus-visible:ring-offset-2 ${
                    active ? "bg-white text-[#3B597B] shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Panels */}
      <section className="pt-6 md:pt-8 pb-14 md:pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {audience === "org" && (
            <div role="tabpanel" id="faq-panel-org" aria-labelledby="faq-tab-org" data-testid="faq-panel-org">
              <FAQList items={ORG_HR_FAQS} keyPrefix="org" />
              <OrgCTA />
            </div>
          )}
          {audience === "emp" && (
            <div role="tabpanel" id="faq-panel-emp" aria-labelledby="faq-tab-emp" data-testid="faq-panel-emp">
              <FAQList items={EMPLOYEE_FAQS} keyPrefix="emp" />
              <EmployeeCTA />
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function FAQList({ items, keyPrefix }) {
  return (
    <ul className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white" data-testid={`faq-list-${keyPrefix}`}>
      {items.map((item, i) => (
        <FAQRow key={`${keyPrefix}-${i}`} id={`${keyPrefix}-${i}`} q={item.q} a={item.a} />
      ))}
    </ul>
  );
}

function FAQRow({ id, q, a }) {
  const [open, setOpen] = useState(false);
  const panelId = `faq-panel-item-${id}`;
  const btnId = `faq-btn-item-${id}`;
  return (
    <li>
      <h3>
        <button
          id={btnId}
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={panelId}
          data-testid={`faq-question-${id}`}
          className="w-full flex items-start justify-between gap-4 text-left px-5 md:px-6 py-4 md:py-5 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4A6FA5]"
        >
          <span className="font-heading text-base md:text-lg text-slate-900 tracking-tight leading-snug">{q}</span>
          <ChevronDown
            className={`w-5 h-5 mt-0.5 shrink-0 text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            strokeWidth={2.2}
            aria-hidden="true"
          />
        </button>
      </h3>
      {open && (
        <div id={panelId} role="region" aria-labelledby={btnId} data-testid={`faq-answer-${id}`} className="px-5 md:px-6 pb-5 md:pb-6 -mt-1 text-sm md:text-base text-slate-700 leading-relaxed">
          {a}
        </div>
      )}
    </li>
  );
}

function OrgCTA() {
  return (
    <div className="mt-10 rounded-2xl p-6 md:p-8 bg-[#F0F4F8] border border-[#4A6FA5]/15" data-testid="faq-org-cta">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-6 h-6 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={2} aria-hidden="true" />
        <div>
          <h3 className="font-heading text-xl md:text-2xl tracking-tight text-slate-900">Considering chaplain care for your organization?</h3>
          <p className="mt-2 text-sm md:text-base text-slate-700 leading-relaxed">
            Explore how CCCHAPS can build a workplace chaplain care arrangement around the needs of your employees and organization.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Link
              to="/for-organizations"
              data-testid="faq-org-primary-cta"
              className="inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider"
            >
              For Organizations <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
            </Link>
            <Link
              to="/schedule"
              data-testid="faq-org-secondary-cta"
              className="inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-lg border border-slate-300 hover:border-[#4A6FA5] text-slate-800 text-sm font-semibold uppercase tracking-wider"
            >
              Schedule an Organizational Consultation
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmployeeCTA() {
  return (
    <div className="mt-10 rounded-2xl p-6 md:p-8 bg-[#F0F4F8] border border-[#4A6FA5]/15" data-testid="faq-emp-cta">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-6 h-6 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={2} aria-hidden="true" />
        <div>
          <h3 className="font-heading text-xl md:text-2xl tracking-tight text-slate-900">Need someone to talk with?</h3>
          <p className="mt-2 text-sm md:text-base text-slate-700 leading-relaxed">
            If your organization participates in CCCHAPS, confidential chaplain support is available when you choose to reach out.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Link
              to="/care-portal/request-support"
              data-testid="faq-emp-primary-cta"
              className="inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider"
            >
              Request Confidential Support <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
            </Link>
            <Link
              to="/callachap"
              data-testid="faq-emp-secondary-cta"
              className="inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-lg border border-slate-300 hover:border-[#4A6FA5] text-slate-800 text-sm font-semibold uppercase tracking-wider"
            >
              Explore CALLACHAP
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
